// 活動管理頁 - Material UI 版本
import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  Chip,
  Avatar,
  useMediaQuery,
  useTheme,
  InputLabel,
  FormControl,
  Select,
  MenuItem as SelectMenuItem,
  Card,
  CardContent,
  Divider,
  Grid,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Dashboard as DashboardIcon,
  QrCodeScanner as ScanIcon,
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  Upload as UploadIcon,
  Visibility as VisibilityIcon,
  FileDownload as ExportIcon,
  Casino as LotteryIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import ProductDashboard from '../../components/ProductDashboard';
import CheckInPage from './CheckInPage';
import CheckInLogPage from './CheckInLogPage';
import OrderAdminPage from './OrderAdminPage';

export default function ProductAdminPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // 狀態管理
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // 表單狀態
  const [formData, setFormData] = useState({
    name: '',
    price: 0,
    stock: 0,
    description: '',
    imageUrl: '',
    startTime: null,
    endTime: null,
    registrationEnd: null,
    isActive: true
  });

  // 匯入/上傳狀態
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Modal 狀態
  const [dashboardModal, setDashboardModal] = useState({ open: false, product: null });
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [scanModal, setScanModal] = useState({ open: false, product: null });
  const [checkInLogsModal, setCheckInLogsModal] = useState({ open: false, product: null });
  const [lotteryModal, setLotteryModal] = useState({ open: false, product: null, list: [], incomplete: [], loading: false });
  const [orderLogsModal, setOrderLogsModal] = useState({ open: false, product: null });
  
  // Menu 狀態
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // 載入數據
  const fetchData = () => {
    setLoading(true);
    fetch('/api/products?includeInactive=true', { credentials: 'include' })
      .then(res => res.json())
      .then(products => {
        setData(products.map(p => ({ ...p, key: p.id })));
        setLoading(false);
      })
      .catch(err => {
        setSnackbar({ open: true, message: '載入失敗: ' + err.message, severity: 'error' });
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  // 處理表單變更
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // 開啟編輯對話框
  const handleEdit = (record) => {
    setEditing(record);
    setFormData({
      name: record.name || '',
      price: record.price || 0,
      stock: record.stock || 0,
      description: record.description || '',
      imageUrl: record.imageUrl || '',
      startTime: record.startTime ? dayjs(record.startTime) : null,
      endTime: record.endTime ? dayjs(record.endTime) : null,
      registrationEnd: record.registrationEnd ? dayjs(record.registrationEnd) : null,
      isActive: record.isActive !== false
    });
    setModalOpen(true);
  };

  // 開啟新增對話框
  const handleAdd = () => {
    setEditing(null);
    setFormData({
      name: '',
      price: 0,
      stock: 0,
      description: '',
      imageUrl: '',
      startTime: null,
      endTime: null,
      registrationEnd: null,
      isActive: true
    });
    setModalOpen(true);
  };

  // 儲存資料
  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        startTime: formData.startTime ? formData.startTime.toISOString() : null,
        endTime: formData.endTime ? formData.endTime.toISOString() : null,
        registrationEnd: formData.registrationEnd ? formData.registrationEnd.toISOString() : null
      };

      const response = await fetch(
        editing ? `/api/products/${editing.id}` : '/api/products',
        {
          method: editing ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        throw new Error(editing ? '更新失敗' : '新增失敗');
      }

      setSnackbar({ 
        open: true, 
        message: editing ? '更新成功' : '新增成功', 
        severity: 'success' 
      });
      setModalOpen(false);
      fetchData();
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.message, 
        severity: 'error' 
      });
    }
  };

  // 刪除資料
  const handleDelete = async (id) => {
    if (!window.confirm('確定要刪除這個活動嗎？')) return;

    try {
      const response = await fetch(`/api/products/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('刪除失敗');
      }

      setSnackbar({ open: true, message: '刪除成功', severity: 'success' });
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  // Modal 處理函數
  const handleDashboard = async (record) => {
    setDashboardModal({ open: true, product: record });
    setDashboardLoading(true);
    setDashboardData(null);
    
    try {
      const response = await fetch(`/api/products/${record.id}/dashboard`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('載入儀表板失敗');
      }
      
      const data = await response.json();
      setDashboardData(data);
    } catch (error) {
      console.error('載入儀表板失敗:', error);
      setSnackbar({ 
        open: true, 
        message: '載入儀表板失敗: ' + error.message, 
        severity: 'error' 
      });
    } finally {
      setDashboardLoading(false);
    }
  };

  const handleScan = (record) => {
    setScanModal({ open: true, product: record });
  };

  const handleCheckInLogs = (record) => {
    setCheckInLogsModal({ open: true, product: record });
  };

  const handleLottery = async (record) => {
    setLotteryModal({ open: true, product: record, list: [], incomplete: [], loading: true });
    try {
      const res = await fetch(`/api/checkin-logs/lottery/${record.id}`);
      if (!res.ok) throw new Error('載入失敗');
      const data = await res.json();
      setLotteryModal({ open: true, product: record, list: data.list || [], incomplete: data.incomplete || [], loading: false });
    } catch (error) {
      setLotteryModal({ open: true, product: record, list: [], incomplete: [], loading: false });
      setSnackbar({ open: true, message: '載入抽獎名單失敗: ' + error.message, severity: 'error' });
    }
  };

  const handleCopyLotteryList = async () => {
    const text = lotteryModal.list.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar({ open: true, message: '已複製名單', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: '複製失敗，請手動選取文字複製', severity: 'error' });
    }
  };

  const handleOrderLogs = (record) => {
    setOrderLogsModal({ open: true, product: record });
  };

  // Menu 處理
  const handleMenuClick = (event, record) => {
    setAnchorEl(event.currentTarget);
    setSelectedRecord(record);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRecord(null);
  };

  // 圖片上傳處理
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('poster', file); // 修正字段名為 'poster'

    try {
      const response = await fetch('/api/upload/poster', { // 修正 API 路徑
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        let errorMessage = '上傳失敗';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || `上傳失敗 (${response.status})`;
        } catch (e) {
          errorMessage = `上傳失敗 (${response.status}: ${response.statusText})`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      handleFormChange('imageUrl', result.imageUrl);
      setSnackbar({ open: true, message: '圖片上傳成功', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  // Excel 匯出
  const handleExport = () => {
    const exportData = data.map(item => ({
      '活動名稱': item.name,
      '價格': item.price,
      '庫存': item.stock,
      '描述': item.description,
      '開始時間': item.startTime ? dayjs(item.startTime).format('YYYY-MM-DD HH:mm') : '',
      '結束時間': item.endTime ? dayjs(item.endTime).format('YYYY-MM-DD HH:mm') : '',
      '報名截止': item.registrationEnd ? dayjs(item.registrationEnd).format('YYYY-MM-DD HH:mm') : '',
      '狀態': item.isActive ? '啟用' : '停用'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '活動列表');
    XLSX.writeFile(wb, '活動列表.xlsx');

    setSnackbar({ open: true, message: '匯出成功', severity: 'success' });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: isMobile ? 0 : 3 }}>
        {/* 標題和操作按鈕 */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: isMobile ? 1.5 : 3,
          px: isMobile ? 2 : 0,
          pt: isMobile ? 2 : 0
        }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              fontSize: isMobile ? '1.5rem' : '2.125rem'
            }}
          >
            活動管理
          </Typography>

          <Stack direction="row" spacing={isMobile ? 1 : 2}>
            <Button
              variant="outlined"
              startIcon={!isMobile && <ExportIcon />}
              onClick={handleExport}
              disabled={data.length === 0}
              size={isMobile ? 'small' : 'medium'}
              sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
            >
              {isMobile ? '匯出' : '匯出 Excel'}
            </Button>
            <Button
              variant="contained"
              startIcon={!isMobile && <AddIcon />}
              onClick={handleAdd}
              size={isMobile ? 'small' : 'medium'}
              sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
            >
              {isMobile ? '新增' : '新增活動'}
            </Button>
          </Stack>
        </Box>

        {/* 資料表格 */}
        <Paper
          elevation={3}
          sx={{
            borderRadius: isMobile ? 0 : 1,
            overflow: 'hidden'
          }}
        >
          <TableContainer>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ px: isMobile ? 1 : 2, py: isMobile ? 1 : 2, fontSize: isMobile ? '0.75rem' : '0.875rem' }}>圖片</TableCell>
                  <TableCell sx={{ px: isMobile ? 1 : 2, py: isMobile ? 1 : 2, fontSize: isMobile ? '0.75rem' : '0.875rem' }}>活動名稱</TableCell>
                  {!isMobile && (
                    <>
                      <TableCell align="right">價格</TableCell>
                      <TableCell align="center">庫存</TableCell>
                      <TableCell>開始時間</TableCell>
                      <TableCell>結束時間</TableCell>
                      <TableCell>報名截止</TableCell>
                      <TableCell align="center">狀態</TableCell>
                    </>
                  )}
                  <TableCell align="center" sx={{ px: isMobile ? 1 : 2, py: isMobile ? 1 : 2, fontSize: isMobile ? '0.75rem' : '0.875rem' }}>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={isMobile ? 3 : 9} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isMobile ? 3 : 9} align="center">
                      暫無資料
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((row) => (
                    <TableRow key={row.id} hover>
                      {/* 圖片 */}
                      <TableCell sx={{ px: isMobile ? 1 : 2, py: isMobile ? 1 : 2 }}>
                        {row.imageUrl ? (
                          <Avatar
                            src={row.imageUrl}
                            variant="rounded"
                            sx={{ width: isMobile ? 40 : 50, height: isMobile ? 40 : 50 }}
                          />
                        ) : (
                          <Avatar variant="rounded" sx={{ width: isMobile ? 40 : 50, height: isMobile ? 40 : 50 }}>
                            📷
                          </Avatar>
                        )}
                      </TableCell>

                      {/* 活動名稱 */}
                      <TableCell sx={{ px: isMobile ? 1 : 2, py: isMobile ? 1 : 2 }}>
                        <Typography variant="body2" sx={{
                          fontSize: isMobile ? '0.75rem' : '0.875rem',
                          maxWidth: isMobile ? '100%' : 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: isMobile ? 'normal' : 'nowrap',
                          lineHeight: isMobile ? 1.3 : 1.5
                        }}>
                          {row.name}
                        </Typography>
                      </TableCell>
                      
                      {/* 桌面版額外欄位 */}
                      {!isMobile && (
                        <>
                          <TableCell align="right">NT${row.price}</TableCell>
                          <TableCell align="center">{row.stock}</TableCell>
                          <TableCell>
                            {row.startTime ? dayjs(row.startTime).format('MM-DD HH:mm') : '-'}
                          </TableCell>
                          <TableCell>
                            {row.endTime ? dayjs(row.endTime).format('MM-DD HH:mm') : '-'}
                          </TableCell>
                          <TableCell>
                            {row.registrationEnd ? dayjs(row.registrationEnd).format('MM-DD HH:mm') : '-'}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={row.isActive ? '啟用' : '停用'}
                              color={row.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </>
                      )}

                      {/* 操作 */}
                      <TableCell align="center" sx={{ px: isMobile ? 0.5 : 2, py: isMobile ? 1 : 2 }}>
                        {isMobile ? (
                          <IconButton
                            onClick={(e) => handleMenuClick(e, row)}
                            size="small"
                            sx={{ padding: 0.5 }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <Stack direction="row" spacing={1}>
                            <IconButton size="small" onClick={() => handleDashboard(row)} title="儀表板">
                              <DashboardIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleScan(row)} title="掃描簽到">
                              <ScanIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleCheckInLogs(row)} title="簽到記錄">
                              <AssignmentIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleLottery(row)} title="抽獎名單">
                              <LotteryIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleOrderLogs(row)} title="報名管理">
                              <PeopleIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleEdit(row)} title="編輯">
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDelete(row.id)} title="刪除" color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* 手機版操作選單 */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => { handleDashboard(selectedRecord); handleMenuClose(); }}>
            <DashboardIcon sx={{ mr: 1 }} /> 儀表板
          </MenuItem>
          <MenuItem onClick={() => { handleScan(selectedRecord); handleMenuClose(); }}>
            <ScanIcon sx={{ mr: 1 }} /> 掃描簽到
          </MenuItem>
          <MenuItem onClick={() => { handleCheckInLogs(selectedRecord); handleMenuClose(); }}>
            <AssignmentIcon sx={{ mr: 1 }} /> 簽到記錄
          </MenuItem>
          <MenuItem onClick={() => { handleLottery(selectedRecord); handleMenuClose(); }}>
            <LotteryIcon sx={{ mr: 1 }} /> 抽獎名單
          </MenuItem>
          <MenuItem onClick={() => { handleOrderLogs(selectedRecord); handleMenuClose(); }}>
            <PeopleIcon sx={{ mr: 1 }} /> 報名管理
          </MenuItem>
          <MenuItem onClick={() => { handleEdit(selectedRecord); handleMenuClose(); }}>
            <EditIcon sx={{ mr: 1 }} /> 編輯
          </MenuItem>
          <MenuItem onClick={() => { handleDelete(selectedRecord?.id); handleMenuClose(); }} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} /> 刪除
          </MenuItem>
        </Menu>

        {/* 編輯/新增對話框 */}
        <Dialog 
          open={modalOpen} 
          onClose={() => setModalOpen(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            {editing ? '編輯活動' : '新增活動'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                label="活動名稱"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                required
                fullWidth
              />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="價格"
                    type="number"
                    value={formData.price}
                    onChange={(e) => handleFormChange('price', Number(e.target.value))}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="庫存"
                    type="number"
                    value={formData.stock}
                    onChange={(e) => handleFormChange('stock', Number(e.target.value))}
                    fullWidth
                  />
                </Grid>
              </Grid>

              <TextField
                label="描述"
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                multiline
                rows={3}
                fullWidth
              />

              <Box>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                  disabled={uploading}
                >
                  {uploading ? '上傳中...' : '上傳圖片'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>
                {formData.imageUrl && (
                  <Box sx={{ mt: 1 }}>
                    <img
                      src={formData.imageUrl}
                      alt="預覽"
                      style={{ maxWidth: 200, maxHeight: 150, objectFit: 'cover' }}
                    />
                  </Box>
                )}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <DateTimePicker
                    label="開始時間"
                    value={formData.startTime}
                    onChange={(value) => handleFormChange('startTime', value)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DateTimePicker
                    label="結束時間"
                    value={formData.endTime}
                    onChange={(value) => handleFormChange('endTime', value)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DateTimePicker
                    label="報名截止"
                    value={formData.registrationEnd}
                    onChange={(value) => handleFormChange('registrationEnd', value)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
              </Grid>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => handleFormChange('isActive', e.target.checked)}
                  />
                }
                label="啟用活動"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setModalOpen(false)}>取消</Button>
            <Button onClick={handleSave} variant="contained">
              {editing ? '更新' : '新增'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal 組件 */}
        <Dialog
          open={dashboardModal.open}
          onClose={() => {
            setDashboardModal({ open: false, product: null });
            setDashboardData(null);
            setDashboardLoading(false);
          }}
          maxWidth="lg"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            {dashboardModal.product?.name} - 儀表板
            <IconButton
              onClick={() => {
                setDashboardModal({ open: false, product: null });
                setDashboardData(null);
                setDashboardLoading(false);
              }}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              ×
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {dashboardModal.product && (
              <ProductDashboard 
                data={dashboardData}
                loading={dashboardLoading}
                productId={dashboardModal.product.id} 
                productName={dashboardModal.product.name} 
              />
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={scanModal.open}
          onClose={() => setScanModal({ open: false, product: null })}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            {scanModal.product?.name} - 掃描簽到
            <IconButton
              onClick={() => setScanModal({ open: false, product: null })}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              ×
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {scanModal.product && (
              <CheckInPage 
                productId={scanModal.product.id} 
                productName={scanModal.product.name} 
              />
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={checkInLogsModal.open}
          onClose={() => setCheckInLogsModal({ open: false, product: null })}
          maxWidth="xl"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            {checkInLogsModal.product?.name} - 簽到記錄
            <IconButton
              onClick={() => setCheckInLogsModal({ open: false, product: null })}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              ×
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {checkInLogsModal.product && (
              <CheckInLogPage
                productId={checkInLogsModal.product.id}
                productName={checkInLogsModal.product.name}
              />
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={lotteryModal.open}
          onClose={() => setLotteryModal({ open: false, product: null, list: [], incomplete: [], loading: false })}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            {lotteryModal.product?.name} - 抽獎名單
            <IconButton
              onClick={() => setLotteryModal({ open: false, product: null, list: [], incomplete: [], loading: false })}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              ×
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {lotteryModal.loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : lotteryModal.list.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                目前尚無已簽到完成的使用者。
              </Typography>
            ) : (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    共 {lotteryModal.list.length} 人
                  </Typography>
                  <Button size="small" onClick={handleCopyLotteryList}>
                    複製名單
                  </Button>
                </Box>
                <TextField
                  multiline
                  fullWidth
                  minRows={4}
                  maxRows={20}
                  value={lotteryModal.list.join('\n')}
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '1rem' } }}
                  onFocus={(e) => e.target.select()}
                />
                {lotteryModal.incomplete.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      另有 {lotteryModal.incomplete.length} 位已簽到但資料不完整，未列入上方名單：
                    </Typography>
                    {lotteryModal.incomplete.map((p, i) => (
                      <Typography key={i} variant="body2">
                        {p.name}{p.lineName ? `（LINE：${p.lineName}）` : ''} — 缺{p.missing}
                      </Typography>
                    ))}
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                      請對方在 LINE 傳「我要抽獎」補填，或到使用者管理補上後重新開啟名單
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={orderLogsModal.open}
          onClose={() => setOrderLogsModal({ open: false, product: null })}
          maxWidth="xl"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            {orderLogsModal.product?.name} - 報名管理
            <IconButton
              onClick={() => setOrderLogsModal({ open: false, product: null })}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              ×
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {orderLogsModal.product && (
              <OrderAdminPage 
                productId={orderLogsModal.product.id} 
                productName={orderLogsModal.product.name} 
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
}